import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAdminUserListQuery, useUpdateUserStatusMutation, useRestoreUserMutation, useBulkDeleteUsersMutation } from '../store/slices/apiSlice';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Toggle } from '../components/ui/Toggle';
import { Table } from '../components/ui/Table';
import {
    Users as UsersIcon,
    Shield,
    Eye,
    Mail,
    Search,
    Filter,
    TrendingUp,
    TrendingDown,
    ChevronLeft,
    ChevronRight,
    Phone,
    Calendar,
    Calendar as CalendarIcon,
    Clock,
    User,
    RotateCcw,
    UserCheck,
    UserX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import UserAvatar from '../components/common/UserAvatar';
import { RootState } from '../store';

const Users: React.FC = () => {

    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');
    const [verificationFilter, setVerificationFilter] = useState('all');
    const [completenessFilter, setCompletenessFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [updateUserStatus] = useUpdateUserStatusMutation();
    const [restoreUser] = useRestoreUserMutation();
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [currentPageUsers, setCurrentPageUsers] = useState<any[]>([]);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
    const [deleteType, setDeleteType] = useState<'soft' | 'hard'>('soft');
    const [bulkDeleteUsers, { isLoading: isBulkDeleting }] = useBulkDeleteUsersMutation();
    const { user } = useSelector((state: RootState) => state.auth);
    const [isFilterActive, setIsFilterActive] = useState(false);

    const handleResetFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setVerificationFilter('all');
        setCompletenessFilter('all');
        setDateFilter('all');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || verificationFilter !== 'all' || completenessFilter !== 'all' || dateFilter !== 'all';


    const { data: userListResponse, isLoading } = useGetAdminUserListQuery({
        page: currentPage,
        limit: 10,
        search: searchTerm || undefined
    });

    const users = userListResponse?.data || [];
    const totalPages = Math.ceil((userListResponse?.total || 0) / 10);

    const formatCurrency = (amount: number | string | null | undefined, currencySymbol: string = '₦') => {
        const value = parseFloat(String(amount || '0')) || 0;
        return `${currencySymbol}${new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value)}`;
    };

    const handleStatusToggle = async (userId: number, currentStatus: boolean) => {
        try {
            const newStatus = !currentStatus;
            const userName = users.find(user => user.user_id === userId)?.name || 'User';

            await updateUserStatus({
                user_id: userId,
                is_active: newStatus
            });

            toast.success(
                `${userName} has been ${newStatus ? 'activated' : 'deactivated'} successfully!`,
            );
        } catch (error) {
            console.error('Failed to update user status:', error);
            toast.error(
                'Failed to update user status. Please try again.',
            );
        }
    };

    const handleRestoreUser = async (userId: number) => {
        try {
            const userName = users.find(user => user.user_id === userId)?.name || 'User';

            await restoreUser({
                user_id: userId
            }).unwrap();

            toast.success(
                `${userName}'s account has been restored successfully!`,
            );
        } catch (error) {
            console.error('Failed to restore user:', error);
            const errorMessage = (error as any)?.data?.message || 'Failed to restore user account. Please try again.';
            toast.error(errorMessage);
        }
    };


    const openViewModal = (userData: any) => {
        navigate(`/admin/user-details/${userData.user_id}`);
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const StatCard: React.FC<{
        title: string;
        value: string | number;
        icon: React.ReactNode;
        trend?: number;
        color: string;
    }> = ({ title, value, icon, trend, color }) => (
        <Card>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-500">{title}</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{value}</p>
                    {trend !== undefined && (
                        <p className={`text-xs sm:text-sm flex items-center mt-1 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {trend >= 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
                            {Math.abs(trend)}% from last month
                        </p>
                    )}
                </div>
                <div className={`p-2 sm:p-3 rounded-full ${color}`}>
                    {React.cloneElement(icon as React.ReactElement, { className: "w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" })}
                </div>
            </div>
        </Card>
    );

    const columns: Array<{
        key: string;
        label: React.ReactNode;
        render?: (value: any, row: any, index: number) => React.ReactNode;
    }> = [
            {
                key: 'selection',
                label: (
                    <input
                        type="checkbox"
                        checked={currentPageUsers.length > 0 && currentPageUsers.every(row => selectedUserIds.includes(row.user_id))}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                                setSelectedUserIds(prev => {
                                    const newSelections = [...prev];
                                    currentPageUsers.forEach(row => {
                                        if (row.user_id !== undefined && !newSelections.includes(row.user_id)) {
                                            newSelections.push(row.user_id);
                                        }
                                    });
                                    return newSelections;
                                });
                            } else {
                                const currentPageIds = currentPageUsers.map(row => row.user_id);
                                setSelectedUserIds(prev => prev.filter(id => !currentPageIds.includes(id)));
                            }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                    />
                ),
                render: (_: any, row: any) => (
                    <input
                        type="checkbox"
                        checked={selectedUserIds.includes(row.user_id)}
                        onChange={(e) => handleSelectUser(row.user_id, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                    />
                ),
            },
            {
                key: 'sno',
                label: 'S.No',
                render: (_: any, __: any, index: number) => (
                    <span className="font-bold text-gray-400 text-xs">#{index + 1}</span>
                ),
            },
            {
                key: 'user',
                label: 'USER',
                render: (_: any, row: any) => (
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <UserAvatar 
                            src={row.profile_image} 
                            name={row.name} 
                            size="sm" 
                        />
                        <div className="min-w-0 flex-1">

                            <div className="text-gray-900 font-medium text-xs sm:text-sm truncate flex items-center gap-1.5">
                                <span>{row.name || 'No Name'}</span>
                                {row.serial_number && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-extrabold text-blue-700 bg-blue-50 border border-blue-100 rounded-md">
                                        #{row.serial_number}
                                    </span>
                                )}
                            </div>
                            <div className="text-gray-700 text-xs sm:text-sm truncate">{row.email}</div>
                        </div>
                    </div>
                ),
            },
            {
                key: 'total_invested',
                label: 'TOTAL INVESTMENT',
                render: (value: string, row: any) => (
                    <span className="text-gray-900 font-medium text-xs sm:text-sm">
                        {formatCurrency(value, row.currency_symbol)}
                    </span>
                ),
            },
            {
                key: 'is_complete_profile',
                label: 'PROFILE STATUS',
                render: (value: boolean) => (
                    <Badge 
                        variant={value ? 'success' : 'secondary'} 
                        className={`text-[10px] px-2.5 py-1 font-bold border-none shadow-sm tracking-tight ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}
                    >
                        {value ? 'COMPLETE' : 'INCOMPLETE'}
                    </Badge>
                ),
            },
            {
                key: 'is_verified',
                label: 'VERIFIED',
                render: (value: boolean) => (
                    <Badge 
                        variant={value ? 'success' : 'warning'} 
                        className={`text-[10px] px-2.5 py-1 font-bold border-none shadow-sm tracking-tight ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                    >
                        {value ? 'VERIFIED' : 'NOT VERIFIED'}
                    </Badge>
                ),
            },
            {
                key: 'is_active',
                label: 'STATUS',
                render: (value: boolean, row: any) => {
                    const isUserActive = value && row.is_complete_profile && row.is_verified;
                    return (
                        <div className="flex items-center space-x-1 sm:space-x-2">
                            {row.is_deleted ? (
                                <Badge variant="danger" className="text-xs px-1.5 sm:px-2 py-0.5 sm:py-1">
                                    Deactivated
                                </Badge>
                            ) : (
                                <Toggle
                                    checked={isUserActive}
                                    disabled={!row.is_complete_profile || !row.is_verified}
                                    onChange={(checked) => handleStatusToggle(row.user_id, !checked)}
                                    size="sm"
                                />
                            )}
                        </div>
                    );
                },
            },
            {
                key: 'created_at',
                label: 'JOINED AT',
                render: (value: string) => (
                    <div className="flex flex-col">
                        <span className="text-gray-800 font-bold text-xs sm:text-sm">
                            {formatDateUtil(value)}
                        </span>
                        <span className="text-gray-500 text-[10px] font-medium">
                            {new Date(value).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                ),
            },
            {
                key: 'actions',
                label: 'ACTIONS',
                render: (_: any, row: any) => (
                    <div className="flex items-center space-x-1 sm:space-x-2">
                        {row.is_deleted && (
                            <Button
                                variant="outline"
                                size="sm"
                                icon={RotateCcw}
                                onClick={() => handleRestoreUser(row.user_id)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs px-2 sm:px-3 py-1 sm:py-2"
                            >
                                <span className="hidden sm:inline">Restore</span>
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            icon={Eye}
                            onClick={() => openViewModal(row)}
                            className="text-xs px-2 sm:px-3 py-1 sm:py-2"
                        >
                            <span className="hidden sm:inline">View</span>
                        </Button>
                    </div>
                ),
            },
        ];

    // Filter users based on selected filters and search term
    const filteredUsers = users.filter(user => {
        const isUserActive = user.is_active && user.is_complete_profile && user.is_verified;
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'active' && isUserActive && !user.is_deleted) ||
            (statusFilter === 'suspended' && !isUserActive && !user.is_deleted) ||
            (statusFilter === 'deactivated' && user.is_deleted);

        const matchesVerification = verificationFilter === 'all' ||
            (verificationFilter === 'verified' && user.is_verified) ||
            (verificationFilter === 'pending' && user.is_pending_verification) ||
            (verificationFilter === 'unverified' && !user.is_verified && !user.is_pending_verification);

        const matchesCompleteness = completenessFilter === 'all' || 
            (completenessFilter === 'complete' && user.profile_completeness === 100) ||
            (completenessFilter === 'incomplete' && user.profile_completeness < 100);

        const matchesSearch =
            !searchTerm ||
            (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));

        // Date Filtering Logic
        let matchesDate = true;
        if (dateFilter !== 'all') {
            const joinDate = new Date(user.created_at);
            const now = new Date();
            
            if (dateFilter === 'today') {
                matchesDate = joinDate.toDateString() === now.toDateString();
            } else if (dateFilter === 'week') {
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(now.getDate() - 7);
                matchesDate = joinDate >= oneWeekAgo;
            } else if (dateFilter === 'month') {
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(now.getMonth() - 1);
                matchesDate = joinDate >= oneMonthAgo;
            } else if (dateFilter === 'custom') {
                if (startDate && endDate) {
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    matchesDate = joinDate >= start && joinDate <= end;
                }
            }
        }

        return matchesSearch && matchesStatus && matchesVerification && matchesCompleteness && matchesDate;
    });

    const handleSelectUser = (userId: number, checked: boolean) => {
        if (checked) {
            setSelectedUserIds(prev => [...prev, userId]);
        } else {
            setSelectedUserIds(prev => prev.filter(id => id !== userId));
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = filteredUsers.map(u => u.user_id).filter(id => id !== undefined);
            setSelectedUserIds(allIds);
        } else {
            setSelectedUserIds([]);
        }
    };

    const handleBulkDelete = async () => {
        try {
            await bulkDeleteUsers({
                user_ids: selectedUserIds,
                delete_type: deleteType
            }).unwrap();

            toast.success(
                `Successfully ${deleteType === 'soft' ? 'soft-deleted' : 'hard-deleted'} ${selectedUserIds.length} ${selectedUserIds.length === 1 ? 'user' : 'users'}.`
            );
            setSelectedUserIds([]);
            setIsBulkDeleteModalOpen(false);
        } catch (error: any) {
            console.error('Bulk delete failed:', error);
            const errorMessage = error?.data?.message || 'Failed to complete deletion. Please try again.';
            toast.error(errorMessage);
        }
    };

    React.useEffect(() => {
        setSelectedUserIds([]);
    }, [searchTerm, statusFilter, verificationFilter, userListResponse]);


    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-48 sm:h-64">
                <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-3 sm:space-y-4 md:space-y-6">
            {/* Header */}
            <PageHeader
                title="User Management"
                description="Manage user accounts and monitor investment activity."
            />

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                <StatCard
                    title="Total Users"
                    value={users.length}
                    icon={<UsersIcon />}
                    color="bg-blue-500"
                />

                <StatCard
                    title="Active Users"
                    value={users.filter(user => user.is_active && user.is_complete_profile && user.is_verified).length}
                    icon={<Shield />}
                    color="bg-green-500"
                />

                <StatCard
                    title="Verified Users"
                    value={users.filter(user => user.is_verified).length}
                    icon={<Eye />}
                    color="bg-purple-500"
                />

                <StatCard
                    title="Deactivated"
                    value={users.filter(user => !user.is_active).length}
                    icon={<UserX />}
                    color="bg-red-500"
                />
            </div>

            {/* Premium Filtering Hub */}
            <Card className="p-6 mb-8 bg-white border-none shadow-xl rounded-2xl overflow-visible">
                <div className="flex flex-col space-y-6">
                    {/* Top Row: Search and Actions */}
                    <div className="flex flex-col lg:flex-row gap-4 items-center">
                        <div className="flex-1 w-full">
                            <div className="relative group">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                                <Input
                                    placeholder="Search by name, email, or user ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-12 h-12 bg-gray-50 border-gray-100 focus:bg-white transition-all rounded-xl shadow-inner"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`h-12 px-6 rounded-xl font-bold flex items-center gap-2 transition-all ${
                                    showFilters ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                <Filter className="w-4 h-4" />
                                Filters
                                {hasActiveFilters && <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />}
                            </Button>
                            {hasActiveFilters && (
                                <Button
                                    variant="outline"
                                    onClick={handleResetFilters}
                                    className="h-12 px-6 border-gray-200 hover:bg-gray-100 rounded-xl font-bold flex items-center gap-2"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Reset
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Expandable Filter Grid */}
                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500 origin-top ${showFilters ? 'opacity-100 scale-y-100 h-auto' : 'opacity-0 scale-y-0 h-0 overflow-hidden'}`}>
                        <div className="space-y-2">
                            <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                                <Shield className="w-3 h-3 mr-1.5" /> Account Status
                            </label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full h-12 px-4 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all cursor-pointer hover:bg-gray-100"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active Only</option>
                                <option value="suspended">Suspended Only</option>
                                <option value="deactivated">Deactivated</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                                <UserCheck className="w-3 h-3 mr-1.5" /> Verification
                            </label>
                            <select
                                value={verificationFilter}
                                onChange={(e) => setVerificationFilter(e.target.value)}
                                className="w-full h-12 px-4 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all cursor-pointer hover:bg-gray-100"
                            >
                                <option value="all">All Verification</option>
                                <option value="verified">Verified</option>
                                <option value="pending">Pending</option>
                                <option value="unverified">Not Verified</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                                <Clock className="w-3 h-3 mr-1.5" /> Profile Completeness
                            </label>
                            <select
                                value={completenessFilter}
                                onChange={(e) => setCompletenessFilter(e.target.value)}
                                className="w-full h-12 px-4 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all cursor-pointer hover:bg-gray-100"
                            >
                                <option value="all">All Completeness</option>
                                <option value="complete">100% Complete</option>
                                <option value="incomplete">Under 100%</option>
                            </select>
                        </div>
                    </div>

                    {/* Date Filters Row */}
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6 pt-4 border-t border-gray-50">
                        <div className="flex flex-wrap gap-2">
                            {[
                                { id: 'all', label: 'All Time' },
                                { id: 'today', label: 'Today' },
                                { id: 'week', label: 'This Week' },
                                { id: 'month', label: 'This Month' },
                                { id: 'custom', label: 'Custom Range' }
                            ].map((period) => (
                                <button
                                    key={period.id}
                                    type="button"
                                    onClick={() => setDateFilter(period.id)}
                                    className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 ${
                                        dateFilter === period.id
                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {period.label}
                                </button>
                            ))}
                        </div>

                        {dateFilter === 'custom' && (
                            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-4 duration-300">
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    />
                                    <div className="absolute -top-5 left-1 text-[9px] font-black text-gray-400 uppercase tracking-tighter">Start</div>
                                </div>
                                <span className="text-gray-300 font-bold">/</span>
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    />
                                    <div className="absolute -top-5 left-1 text-[9px] font-black text-gray-400 uppercase tracking-tighter">End</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* Results Header */}
            <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest">User Directory</h3>
                    <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-none font-bold rounded-full">
                        {filteredUsers.length} Users Found
                    </Badge>
                </div>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                data={filteredUsers}
                itemsPerPage={10}
                getRowClassName={(row) => row.is_deleted ? 'bg-red-50/40 hover:bg-red-50/60' : ''}
                onPageDataChange={setCurrentPageUsers}
            />

            {/* Floating Bulk Actions Bar */}
            {selectedUserIds.length > 0 && (
                <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl z-50 flex items-center gap-6 border border-gray-800 transition-all duration-300">
                    <span className="text-sm font-semibold">
                        {selectedUserIds.length} {selectedUserIds.length === 1 ? 'user' : 'users'} selected
                    </span>
                    <div className="h-6 w-px bg-gray-800"></div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setDeleteType('soft');
                                setIsBulkDeleteModalOpen(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                        >
                            Soft Delete
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setDeleteType('hard');
                                setIsBulkDeleteModalOpen(true);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                        >
                            Hard Delete
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedUserIds([])}
                            className="text-gray-400 hover:text-white px-2 py-2 text-xs font-semibold transition-colors duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            <Modal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                title={`${deleteType === 'soft' ? 'Soft' : 'Hard'} Delete Selected Users`}
            >
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to {deleteType === 'soft' ? 'soft delete' : 'permanently hard delete'}{' '}
                        <span className="font-semibold text-gray-900">{selectedUserIds.length}</span> selected {selectedUserIds.length === 1 ? 'user' : 'users'}?
                    </p>
                    {deleteType === 'hard' && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs sm:text-sm font-medium mb-6">
                            <span className="font-bold uppercase mt-0.5">Warning:</span>
                            <span>
                                This action is permanent and cannot be undone. All investments, bookings, wallet transactions, and history associated with these accounts will be permanently destroyed.
                            </span>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setIsBulkDeleteModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={deleteType === 'soft' ? 'warning' : 'danger'}
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                        >
                            {isBulkDeleting ? 'Deleting...' : `Confirm ${deleteType === 'soft' ? 'Soft' : 'Hard'} Delete`}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Users;